import Ionicons from "@expo/vector-icons/Ionicons";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { notify } from "@/lib/dialog";
import {
  backgroundRoutePreview,
  requestBackgroundLocation,
  startStayVisible,
  type BackgroundRoutePreviewResult,
} from "@/lib/stay-visible";
import {
  promptPlan,
  repairPlan,
  type PromptNotice,
  type RepairNotice,
} from "@/lib/stay-visible-plans";
import { useStayVisibleStore } from "@/state/stay-visible-store";

/**
 * The OS's own name for the setting being asked for. iOS and Android word it
 * differently, and quoting the wrong one sends the user hunting for a row that
 * isn't there.
 */
function alwaysSettingName(t: TFunction): string {
  return Platform.OS === "android"
    ? t("visibility.settingAllowAllTheTime")
    : t("visibility.settingAlwaysAllow");
}

/**
 * Where the button is actually going to send you — `null` until it is known.
 *
 * Labels the control. On iOS the OS stops offering its "Always Allow" prompt
 * after the first attempt and the request then resolves with nothing on screen,
 * so a button reading "Always Allow" either does nothing (the reported bug) or,
 * once fixed, launches Settings without saying so. Neither is honest.
 *
 * Tri-state on purpose. Deciding now involves reading storage as well as the
 * OS, so an initial `false` would paint "Always Allow", then swap to "Open
 * settings" under the user's thumb. `null` means "still working it out" and the
 * caller shows the spinner it already has for `working`.
 *
 * `null` therefore also DISABLES the button, which makes "never settles" a
 * permanently dead control rather than a cautious one. Two things stop that:
 * `backgroundRoutePreview` returns a NON-NULL result, so the type itself rules
 * out resolving to "not yet known"; and the rejection path is caught here
 * rather than trusted not to happen, because the cost of being wrong is a user
 * staring at "Requesting…" with nothing to press.
 *
 * Note the two senses of "unknown" that this paragraph used to conflate. The
 * preview CAN resolve to the route `"unknown"` — meaning "we could not work out
 * where the button should go" — and that is a settled answer which labels the
 * button neutrally and leaves it pressable. What it may never do is fail to
 * settle at all, which is what `null` means here.
 *
 * Re-read every time the sheet becomes visible rather than once at mount: the
 * answer changes while the app is backgrounded — a trip to Settings is exactly
 * how — so a sheet reopened afterwards must not still carry the old label.
 */
function useBackgroundRoute(visible: boolean): BackgroundRoutePreviewResult | null {
  const [route, setRoute] = useState<BackgroundRoutePreviewResult | null>(null);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    void backgroundRoutePreview()
      .then((next) => {
        if (!cancelled) setRoute(next);
      })
      .catch(() => {
        // We could not work it out. "unknown" is the honest answer — the tap
        // still reaches `requestBackgroundLocation`, which explains itself.
        // Not "unavailable": that asserts the platform cannot do this at all.
        if (!cancelled) setRoute("unknown");
      });
    // Forgotten on the way OUT, in cleanup, rather than on the way in. Clearing
    // it in the effect body would be a synchronous setState inside an effect —
    // the cascading-render rule — and this reads better anyway: closing the
    // sheet is the moment the answer stops being current.
    return () => {
      cancelled = true;
      setRoute(null);
    };
  }, [visible]);

  return route;
}

/**
 * The "Stay visible while you're here?" consent prompt. Shown after a user
 * becomes visible, to (optionally) upgrade to background visibility. Driven by
 * the stay-visible store's `promptVenueId`.
 */
export function StayVisibleModal() {
  const c = useTheme();
  const { t } = useTranslation();
  const promptVenueId = useStayVisibleStore((s) => s.promptVenueId);
  const resolve = useStayVisibleStore((s) => s.resolve);
  const dismiss = useStayVisibleStore((s) => s.dismissPrompt);
  const [working, setWorking] = useState(false);

  const visible = promptVenueId !== null;
  const venueId = promptVenueId || null;
  const setting = alwaysSettingName(t);
  const route = useBackgroundRoute(visible);
  const settling = route === null;

  /**
   * The words for each outcome. The CHOICE of outcome is `promptPlan`'s, in a
   * module a test can drive; this is only the lookup.
   */
  function promptNotice(notice: PromptNotice): [string, string] {
    switch (notice) {
      case "backgroundOff":
        return [
          t("visibility.backgroundOffTitle"),
          t("visibility.backgroundOffBody", { setting }),
        ];
      case "unavailable":
        return [t("visibility.notAvailableTitle"), t("visibility.notAvailableBody")];
      case "temporaryFailure":
        return [
          t("visibility.temporaryFailureTitle"),
          t("visibility.temporaryFailureBody"),
        ];
    }
  }

  async function onOkay() {
    if (working) return;
    setWorking(true);

    let result;
    try {
      result = await requestBackgroundLocation();
    } finally {
      // Scoped to the REQUEST only, deliberately. `startStayVisible` below is
      // still unbounded, and holding the spinner across it would trade a rare
      // stuck button for a common one.
      setWorking(false);
    }

    // No result switch here. `promptPlan` owns that decision exhaustively, and
    // a second one in this component would be free to disagree with it — which
    // is how a timeout came to persist `while_open_only` and disable the
    // feature for good.
    const plan = promptPlan(result);
    // `resolve` is what closes this Modal, and it is CONDITIONAL — an outcome
    // with a notice but no mode used to leave the prompt up and raise the
    // notice over it, which iOS turns into an invisible touch-eating window.
    // Dismiss unconditionally before the notice; the prompt has done its job
    // either way.
    if (plan.mode) resolve(plan.mode, venueId);
    else if (plan.notice) dismiss();
    if (plan.start) void startStayVisible();
    if (plan.notice) notify(...promptNotice(plan.notice));
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={dismiss}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={t("common.dismiss")}
          style={styles.backdrop}
          onPress={dismiss}
        />

        <View
          style={[
            styles.sheet,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          <View
            style={[styles.icon, { backgroundColor: c.backgroundSelected }]}
          >
            <Ionicons name="eye-outline" size={24} color={c.tint} />
          </View>

          <ThemedText variant="subtitle" center>
            {t("visibility.stayVisiblePromptTitle")}
          </ThemedText>

          <ThemedText variant="body" tone="secondary" center>
            {t("visibility.stayVisiblePromptBody", { setting })}
          </ThemedText>

          <ThemedText variant="caption" tone="muted" center>
            {t("visibility.stayVisiblePromptLocationNote")}
          </ThemedText>

          <ThemedText variant="caption" tone="muted" center>
            {t("visibility.stayVisiblePromptControlNote")}
          </ThemedText>

          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              onPress={onOkay}
              disabled={working || settling}
              style={[
                styles.primaryBtn,
                { backgroundColor: c.tint, opacity: working ? 0.7 : 1 },
              ]}
            >
              <ThemedText variant="bodyStrong" tone="onTint">
                {working || settling
                  ? t("common.requesting")
                  : route === "settings"
                    ? t("common.openSettings")
                    : t("common.continue")}
              </ThemedText>
            </Pressable>

          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Quiet repair prompt shown after a background break is detected (Always
 * permission revoked, or the background task couldn't run). By the time this
 * shows, the user has already been set to app-open-only + marked hidden.
 */
export function StayVisibleRepairModal() {
  const c = useTheme();
  const { t } = useTranslation();
  const repair = useStayVisibleStore((s) => s.repair);
  const clearRepair = useStayVisibleStore((s) => s.clearRepair);
  const setMode = useStayVisibleStore((s) => s.setMode);
  const verify = useStayVisibleStore((s) => s.verify);
  const [working, setWorking] = useState(false);

  const visible = repair !== null;
  const revoked = repair === "background_revoked";
  const setting = alwaysSettingName(t);
  const route = useBackgroundRoute(visible);
  const settling = route === null;

  function repairNotice(notice: RepairNotice): [string, string] {
    switch (notice) {
      case "stillOff":
        return [
          t("visibility.stillOffTitle"),
          t("visibility.stillOffBody", { setting }),
        ];
      case "temporaryFailure":
        return [
          t("visibility.temporaryFailureTitle"),
          t("visibility.temporaryFailureBody"),
        ];
    }
  }

  async function allowAlways() {
    if (working) return;
    setWorking(true);

    let result;
    try {
      result = await requestBackgroundLocation();
    } finally {
      // Before the awaits below, exactly as it was: `startStayVisible` is
      // unbounded tonight, and the spinner must not wait on it.
      setWorking(false);
    }

    const plan = repairPlan(result);
    if (plan.mode) setMode(plan.mode);
    if (plan.start) await startStayVisible();
    if (plan.verify) void verify();
    // Same shape as the prompt above: `clearRepair` is what closes this Modal
    // and it is conditional, so an outcome carrying a notice but not
    // `clearRepair` raised a dialog over an open Modal — the iOS wedge. A
    // notice means this sheet is finished talking either way.
    if (plan.clearRepair || plan.notice) clearRepair();
    if (plan.notice) notify(...repairNotice(plan.notice));
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={clearRepair}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={t("common.dismiss")}
          style={styles.backdrop}
          onPress={clearRepair}
        />
        <View
          style={[
            styles.sheet,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          <View style={[styles.icon, { backgroundColor: c.dangerTint }]}>
            <Ionicons name="eye-off-outline" size={24} color={c.danger} />
          </View>

          <ThemedText variant="subtitle" center>
            {revoked
              ? t("visibility.repairRevokedTitle")
              : t("visibility.repairBrokenTitle")}
          </ThemedText>

          <ThemedText variant="body" tone="secondary" center>
            {t("visibility.repairNowAppOpenOnly")}
            {revoked
              ? ` ${t("visibility.repairAllowAlwaysHint", { setting })}`
              : ""}
          </ThemedText>

          <View style={styles.actions}>
            {revoked ? (
              <Pressable
                onPress={allowAlways}
                disabled={working || settling}
                style={[
                  styles.primaryBtn,
                  { backgroundColor: c.tint, opacity: working ? 0.7 : 1 },
                ]}
              >
                <ThemedText variant="bodyStrong" tone="onTint">
                  {working || settling
                    ? t("common.requesting")
                    : route === "settings"
                      ? t("common.openSettings")
                      : setting}
                </ThemedText>
              </Pressable>
            ) : null}

            <Pressable
              onPress={clearRepair}
              style={[styles.secondaryBtn, { borderColor: c.border }]}
            >
              <ThemedText variant="bodyStrong" tone="tint">
                {t("visibility.keepAppOpenOnly")}
              </ThemedText>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, alignItems: "center", justifyContent: "center" },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.55)",
  },
  sheet: {
    width: "88%",
    maxWidth: 420,
    padding: Spacing.xl,
    gap: Spacing.md,
    alignItems: "center",
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  icon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: Spacing.xs,
  },
  actions: { width: "100%", gap: Spacing.sm, marginTop: Spacing.sm },
  primaryBtn: {
    minHeight: 52,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  secondaryBtn: {
    minHeight: 50,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
});
